import React, {useEffect, useState} from "react";
import styled from "styled-components";
import {DisplayOnly, media, CarouselProductsList} from "@ui";
import useRouter from "@mock/hooks/useRouter";
import useAppliedFilters from "@mock/hooks/useAppliedFilters";
import AppliedFilters from "@themes/boost/components/AppliedFilters";
import SortAndFilterActions from "@themes/boost/components/SortAndFilterActions";
import useSortAndFilterHeader from "@mock/hooks/useSortAndFilterHeader";
import {useThemeComponents} from "@mock/hooks/useThemeComponents";

export default function BoostThemeProductsPage({title = null, ...props}) {
  const {
    BasePage,
    ShowcaseDealsList,
    ShowcaseProductsList,
    ProductsOrderSelector,
    ShowcaseRecommendedProductsList,
  } = useThemeComponents();
  const {FiltersWrapperComponent, site} = useSortAndFilterHeader();
  const router = useRouter();
  const [paramFilters] = useAppliedFilters();

  const [filters, setFilters] = useState(paramFilters);
  const {inlineOnLargeScreen} = site.getUiConfiguration().filters;

  const {productId} = router.query;

  const mediaDimensions = {
    lg: 1440,
  };

  useEffect(() => {
    if (!productId && !paramFilters.equals(filters)) {
      setFilters(paramFilters);
    }
  }, [productId, setFilters, paramFilters, filters]);

  return (
    <BasePage disableMaxWidth={true} {...props}>
      <BrowserWithFiltersContainer>
        {inlineOnLargeScreen && (
          <DisplayOnly dims={["lg"]} overrideDimensions={mediaDimensions}>
            <FiltersWrapperComponent
              mode="inline"
              header={
                <CustomHeader>
                  <ProductsOrderSelector />
                </CustomHeader>
              }
            />
          </DisplayOnly>
        )}
        <Container inlineOnLargeScreen={inlineOnLargeScreen}>
          <DisplayOnly
            dims={inlineOnLargeScreen ? ["md", "sm"] : ["md", "sm", "lg"]}
            overrideDimensions={mediaDimensions}
          >
            <AppliedFilters filters={filters} />
          </DisplayOnly>
          <ProductsContainer inlineOnLargeScreen={inlineOnLargeScreen}>
            {title}
            {!inlineOnLargeScreen && <SortAndFilterActions />}
            {inlineOnLargeScreen && (
              <DisplayOnly
                dims={inlineOnLargeScreen ? ["md", "sm"] : ["md", "sm", "lg"]}
                overrideDimensions={mediaDimensions}
              >
                <SortAndFilterActions />
              </DisplayOnly>
            )}
            <ShowcaseDealsList />
            <ShowcaseRecommendedProductsList
              filters={filters}
              ProductsListComponent={CarouselProductsList}
            />
            <ShowcaseProductsList />
          </ProductsContainer>
        </Container>
      </BrowserWithFiltersContainer>
    </BasePage>
  );
}

const BrowserWithFiltersContainer = styled.div`
  @media (min-width: 1440px) {
    max-width: ${({inlineOnLargeScreen}) => (inlineOnLargeScreen ? "1440px" : "unset")};
    margin: 0 auto;
  }

  position: relative;
  display: flex;
  justify-content: center;

  .inline-filters__container {
    padding-top: ${({theme}) => theme.v2?.spacing(2)};
  }
`;

const CustomHeader = styled.div`
  margin-bottom: ${({theme}) => theme.v2?.spacing(8)};
`;

const ProductsContainer = styled.div`
  width: 100%;

  @media (min-width: 1560px) {
    max-width: ${({inlineOnLargeScreen}) =>
      inlineOnLargeScreen ? "1150px" : "none"} !important;
  }

  @media (min-width: 1440px) and (max-width: 1559px) {
    max-width: ${({inlineOnLargeScreen}) =>
      inlineOnLargeScreen ? "1070px" : "none"} !important;
  }

  .sort-filters__container {
    padding: 0 32px 32px;
  }

  ${media.down("md")} {
    .sort-filters__container {
      padding: 0 16px 32px;
    }
  }
`;

const Container = styled.div`
  padding-top: 20px;

  > section > header,
  > section > p {
    padding: 0 32px ${media.down("md")} {
      padding: 0 16px;
    }
  }

  width: 100%;
  margin: 0 auto;
  box-sizing: border-box;

  @media (min-width: 1560px) {
    max-width: ${({inlineOnLargeScreen}) => (inlineOnLargeScreen ? "1150px" : "none")};
  }

  @media (min-width: 1440px) and (max-width: 1559px) {
    max-width: ${({inlineOnLargeScreen}) => (inlineOnLargeScreen ? "1070px" : "none")};
  }

  @media (min-width: 1440px) {
    margin: ${({inlineOnLargeScreen}) => (inlineOnLargeScreen ? 0 : "0 auto")};

    ${({theme, inlineOnLargeScreen}) =>
      !inlineOnLargeScreen
        ? ""
        : `
      position: relative;
      display: flex;
      justify-content: center;
      padding: 0 ${theme.v2.spacing(8)};
  
      .carousel-card-list__container {
        position: relative;
        > div:first-child {
          > button {
            position: absolute;
            left: -300px;
            top: 50%;
            transform: translate3d(-50px, -50%, 0);
          }
        }
        > div:last-child {
          > button {
            position: absolute;
            right: -50px;
            top: 50%;
            transform: translate3d(50px, -50%, 0) rotate(180deg);
          }
        }
      }
    `};
  }
`;
